import Cookies from 'js-cookie';

/**
 * CookieService class provides utility methods for interacting with cookies.
 * It includes methods to get, set, and remove cookies, with added support for:
 * - Parsing cookie values as JSON
 * - Validating cookie values against a provided schema
 * - Handling options for customization of cookie behavior
 *
 * Usage:
 * - Use `get()` to retrieve a cookie value, with optional JSON parsing and validation.
 * - Use `set()` to store a cookie with a stringified value (automatically JSON-stringifies non-string values).
 * - Use `remove()` to delete a specified cookie.
 * - Use `getAll()` to retrieve all cookies.
 */
class CookieService {
  /**
   * Retrieves the value of a specified cookie.
   * Optionally parses the value as JSON and validates it against a schema.
   * If the validation fails, an exception will be thrown.
   * If the cookie doesn't exist, it returns `undefined`.
   *
   * @param {string} key - The name of the cookie.
   * @param {object} [options] - Optional options for parsing and validation.
   * @param {boolean} [options.parseJSON] - Flag to indicate whether the cookie value should be parsed as JSON.
   * @param {object} [options.validationSchema] - A schema to validate the cookie value against (e.g., using a validation library like Zod or Joi).
   * @returns {any} - The cookie value, optionally parsed and validated.
   */
  static get(key, options) {
    const value = Cookies.get(key);

    if (typeof value === 'undefined') return value;

    let mutatedValue = value;

    if (options?.parseJSON) {
      try {
        mutatedValue = JSON.parse(value);
      } catch (err) {
        console.error('Error parsing cookie value:', err);
      }
    }

    if (options?.validationSchema) {
      return options.validationSchema.parse(mutatedValue);
    }

    return mutatedValue;
  }

  /**
   * Retrieves all cookies stored in the browser.
   *
   * @returns {object} - An object containing all cookies, where the keys are the cookie names and the values are the cookie values.
   */
  static getAll() {
    return Cookies.get();
  }

  /**
   * Sets a cookie with a specified key and value.
   * Non-string values are automatically stringified (JSON.stringify).
   *
   * @param {string} key - The name of the cookie.
   * @param {any} value - The value to store in the cookie. Non-string values will be stringified.
   * @param {object} [options] - Optional cookie options (e.g., expiration time, path, etc.).
   * @returns {undefined} - No return value, but the cookie will be set in the browser.
   */
  static set(key, value, options) {
    const stringValue =
      typeof value === 'string' ? value : JSON.stringify(value);
    return Cookies.set(key, stringValue, options);
  }

  /**
   * Removes a specified cookie by its key.
   *
   * @param {string} key - The name of the cookie to remove.
   * @param {object} [options] - Optional options for cookie removal (e.g., path).
   * @returns {undefined} - No return value, but the specified cookie will be removed.
   */
  static remove(key, options) {
    return Cookies.remove(key, options);
  }
}

export default CookieService;
